import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Snackbar,
  Alert,
  Modal,
  Backdrop,
  Fade,
  List,
  ListItemButton,
  ListItemText,
  Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { AppContext } from "../../../context/AppContext";

const BrandManager = () => {
  const { brands = [], refreshBrands, csrfFetch } = useContext(AppContext) || {};

  const [search, setSearch] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [editingBrand, setEditingBrand] = useState(null);
  const [editedBrandName, setEditedBrandName] = useState("");
  const [editedCode, setEditedCode] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const safeBrands = Array.isArray(brands) ? brands : [];

  const sortedBrands = useMemo(() => {
    return [...safeBrands].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base"
      })
    );
  }, [safeBrands]);

  const filteredBrands = useMemo(() => {
    if (!search.trim()) return sortedBrands;
    return sortedBrands.filter((b) =>
      String(b?.name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [sortedBrands, search]);

  const handleAddBrand = async () => {
    if (!newBrand.trim()) return;

    try {
      const response = await csrfFetch("/categorias/marcas/agregar/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newBrand.trim() })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(result.error || "No se pudo agregar la marca.");
        return;
      }

      setNewBrand("");
      setSuccessMessage("Marca añadida correctamente.");
      await refreshBrands();
    } catch {
      setError("No se pudo agregar la marca.");
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setEditedBrandName(brand?.name || "");
    setEditedCode(brand?.sequential_code || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingBrand(null);
    setEditedBrandName("");
    setEditedCode("");
  };

  const handleSaveEdit = async () => {
    if (!editingBrand || !editedBrandName.trim() || !editedCode.trim()) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    try {
      const response = await csrfFetch(`/categorias/marcas/${editingBrand.id}/editar/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editedBrandName.trim(),
          sequential_code: editedCode.trim()
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(result.error || "No se pudo editar la marca.");
        return;
      }

      setSuccessMessage("Marca editada correctamente.");
      handleCloseModal();
      await refreshBrands();
    } catch {
      setError("No se pudo editar la marca.");
    }
  };

  const handleDeleteBrand = async () => {
    if (!editingBrand) return;

    try {
      const response = await csrfFetch(`/categorias/marcas/${editingBrand.id}/eliminar/`, {
        method: "DELETE"
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(result.error || "No se pudo eliminar la marca.");
        return;
      }

      setSuccessMessage("Marca eliminada correctamente.");
      handleCloseModal();
      await refreshBrands();
    } catch {
      setError("No se pudo eliminar la marca.");
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Gestión de Marcas
      </Typography>

      <Box display="flex" gap={1} mb={2}>
        <TextField
          fullWidth
          size="small"
          label="Nueva marca"
          value={newBrand}
          onChange={(e) => setNewBrand(e.target.value)}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBrand}
          sx={{
            textTransform: "none",
            bgcolor: "#1565C0",
            "&:hover": { bgcolor: "#0D47A1" },
            px: 2.2,
            fontWeight: 700,
          }}
        >
          Añadir
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar marca..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      <Paper
        variant="outlined"
        sx={{
          maxHeight: 420,
          overflowY: "auto"
        }}
      >
        <List dense disablePadding>
          {filteredBrands.map((brand) => (
            <ListItemButton
              key={brand.id}
              onClick={() => handleEditBrand(brand)}
            >
              <ListItemText
                primary={brand.name}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              p: 3,
              borderRadius: 2,
              width: 320
            }}
          >
            <Typography mb={2}>
              Editar marca
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Código"
              value={editedCode}
              onChange={(e) => setEditedCode(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Nombre"
              value={editedBrandName}
              onChange={(e) => setEditedBrandName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={handleDeleteBrand}
              >
                Eliminar
              </Button>

              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveEdit}
              >
                Guardar
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandManager;